import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { resetStoreAction } from 'state/commonActions';

// Define the lease term type to match the API response from DateBasedLeaseTermSelector
export interface LeaseTermDateBased {
  id: string;
  leaseStartWindowId: string;
  startDate: string;
  endDate: string;
  name: string;
  isImmediateMoveIn: boolean;
  maxMoveInDate: string;
  isDefault: boolean;
}

type StudentUnitReducer = {
  availableSpaceConfigurationIds: number[];
  selectedPreferredAmenityIds: number[];
  selectedLeaseTermId?: string;
  selectedLeaseStartWindowId?: string;
  immediateMoveInMaxDate?: string;
  selectedSpace?: {
    unitSpaceId: string;
    spaceConfigurationId: string;
  };
};

export type StudentUnitSelectionReducer = StudentUnitReducer;

const initialState: StudentUnitSelectionReducer = {
  availableSpaceConfigurationIds: [],
  selectedPreferredAmenityIds: [],
  selectedLeaseTermId: undefined,
  selectedLeaseStartWindowId: undefined,
  immediateMoveInMaxDate: undefined,
  selectedSpace: undefined,
};

export const studentUnitSelectionSlice = createSlice({
  name: 'studentUnitSelection',
  initialState,
  reducers: {
    updateStudentUnitSelection: (state, action: PayloadAction<Partial<StudentUnitReducer>>) => {
      return { ...state, ...action.payload };
    },
    updateAvailableSpaceConfigurationIds: (state, action: PayloadAction<number[]>) => {
      return { ...state, availableSpaceConfigurationIds: action.payload };
    },
    updateSelectedPreferredAmenityIds: (state, action: PayloadAction<number[]>) => {
      return { ...state, selectedPreferredAmenityIds: action.payload };
    },
    updateLeaseTerm: (state, action: PayloadAction<{ id: string; leaseStartWindowId: string }>) => {
      return {
        ...state,
        selectedLeaseTermId: action.payload.id,
        selectedLeaseStartWindowId: action.payload.leaseStartWindowId,
      };
    },
    updateSelectedSpace: (
      state,
      action: PayloadAction<{
        unitSpaceId: string;
        spaceConfigurationId: string;
      }>,
    ) => {
      return {
        ...state,
        selectedSpace: action.payload,
      };
    },
    setImmediateMoveInMaxDate: (state, action: PayloadAction<string | undefined>) => {
      return { ...state, immediateMoveInMaxDate: action.payload };
    },
    resetStudentUnitSelection: (state) => {
      return {
        ...state,
        availableSpaceConfigurationIds: [],
        selectedPreferredAmenityIds: [],
        selectedLeaseTermId: undefined,
        selectedLeaseStartWindowId: undefined,
        immediateMoveInMaxDate: undefined,
        selectedSpace: undefined,
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetStoreAction, () => initialState);
  },
});

export const {
  updateStudentUnitSelection,
  updateAvailableSpaceConfigurationIds,
  updateSelectedPreferredAmenityIds,
  updateLeaseTerm,
  updateSelectedSpace,
  setImmediateMoveInMaxDate,
  resetStudentUnitSelection,
} = studentUnitSelectionSlice.actions;

export default studentUnitSelectionSlice.reducer;
